/**
 * Cookie-consent copy for Ophelos v2. The single source of the per-category
 * copy (title + body), shared by the banner (per-category label + expandable
 * detail) and the standalone cookie-policy page so both stay in sync and
 * translated. The banner falls back to the governance category label only when
 * a key has no entry here. Framework-agnostic plain data: presentational
 * components pick a language and render it.
 *
 * The `key`s match the governance `cookie_consent.categories` keys, so a
 * category's description can be looked up by key.
 */

export type Language = 'en' | 'pt' | 'fr';

export interface CookieCategoryCopy {
  /** Matches the governance category key (essential, analytics, marketing, functional). */
  key: string;
  title: string;
  body: string;
}

/** Static chrome of the consent banner (headings, buttons, labels). */
export interface CookieBannerUiCopy {
  bannerAriaLabel: string;
  /** Heading on the summary bar. */
  summaryHeading: string;
  managePreferences: string;
  rejectAll: string;
  acceptAll: string;
  readPolicy: string;
  /** Title of the preferences dialog. */
  preferencesTitle: string;
  /** Badge on the non-toggleable essential category. */
  alwaysOn: string;
  /** Expandable per-category detail control. */
  details: string;
  back: string;
  savePreferences: string;
}

export interface CookieConsentCopy {
  /** Page/banner title. */
  title: string;
  intro: string;
  /** Shown after the categories: the consequence of declining/withdrawing. */
  declineNote: string;
  categories: CookieCategoryCopy[];
  ui: CookieBannerUiCopy;
}

const EN: CookieConsentCopy = {
  title: 'Cookie Policy',
  intro:
    "We use cookies to run this site securely and, with your permission, to understand how it's used and to support marketing & analytics. You can turn optional cookies on or off below, and change your mind at any time.",
  declineNote: 'Note, if you choose to decline all cookies, or if you later withdraw your consent, our website may not function properly.',
  categories: [
    {
      key: 'essential',
      title: 'Strictly necessary',
      body: "Required to keep you signed in, remember your settings, and keep the site secure. These can't be switched off because the site won't work without them.",
    },
    {
      key: 'analytics',
      title: 'Analytics',
      body: 'Helps us understand how people use the site - which pages are visited and where things go wrong, so we can continuously improve it. All information these cookies collect is aggregated. No data is used to identify you personally. If you do not allow these cookies, we will not know when you have visited our Services and will not be able to monitor its performance.',
    },
    {
      key: 'marketing',
      title: 'Marketing',
      body: 'Marketing cookies are used to track visitors across the website. They may be used to show you relevant offers and measure how well they work, both on this site and elsewhere.',
    },
    {
      key: 'functional',
      title: 'Functional',
      body: "Remembers choices you've made, like language or region, so you don't have to set them again each visit. These cookies are used to recognise you when you return to our Services or to enable the Services to provide enhanced functionality and personalisation. They may be set by us or by third party providers whose services we have added to our pages. If you do not allow these cookies then some or all of the Services may not function properly.",
    },
  ],
  ui: {
    bannerAriaLabel: 'Cookie consent',
    summaryHeading: 'Can we use optional cookies?',
    managePreferences: 'Manage preferences',
    rejectAll: 'Reject all',
    acceptAll: 'Accept all',
    readPolicy: 'Read our cookie policy',
    preferencesTitle: 'Cookie preferences',
    alwaysOn: 'Always on',
    details: 'Details',
    back: 'Back',
    savePreferences: 'Save preferences',
  },
};

const PT: CookieConsentCopy = {
  title: 'Política de Cookies',
  intro:
    'Este website utiliza cookies para garantir a sua segurança e, mediante o seu consentimento, para recolher informação estatística sobre a sua utilização, para apoiar atividades de marketing e análise. Pode ativar ou desativar abaixo os cookies opcionais e alterar as suas preferências a qualquer momento.',
  declineNote:
    'Nota: Se recusar os cookies opcionais ou retirar posteriormente o seu consentimento, algumas funcionalidades do website poderão ficar limitadas ou não funcionar corretamente. Os cookies estritamente necessários permanecerão ativos, por serem indispensáveis ao funcionamento e à segurança do website.',
  categories: [
    {
      key: 'essential',
      title: 'Estritamente necessários',
      body: 'Necessários para o manter com sessão iniciada, guardar as suas preferências e garantir a segurança e o funcionamento do site. Estes cookies não podem ser desativados, o website não funcionará corretamente sem eles.',
    },
    {
      key: 'analytics',
      title: 'Análise',
      body: 'Ajudam-nos a compreender como utilizam o website — quais as páginas visitadas e onde ocorrem problemas — para que possamos melhorá-lo continuamente. As informações recolhidas são agregadas e não são utilizadas para o identificar pessoalmente. Se não permitir estes cookies, não saberemos quando visitou os nossos Serviços e não poderemos monitorizar o respetivo desempenho.',
    },
    {
      key: 'marketing',
      title: 'Marketing',
      body: 'Os cookies de marketing são utilizados para monitorizar a utilização do website. Podem ser utilizados para lhe apresentar conteúdos relevantes, bem como medir a sua eficácia, tanto neste website como noutros canais.',
    },
    {
      key: 'functional',
      title: 'Funcionais',
      body: 'Memorizam as escolhas que efetuou, como o idioma ou a região, para que não tenha de as configurar em cada visita. Estes cookies são utilizados para o reconhecer quando regressa aos nossos Serviços ou para permitir que os Serviços ofereçam funcionalidades melhoradas e personalizadas. Podem ser definidos por nós ou por fornecedores terceiros cujos serviços adicionámos às nossas páginas. Se não permitir estes cookies, algumas funcionalidades poderão não estar disponíveis ou funcionar corretamente.',
    },
  ],
  ui: {
    bannerAriaLabel: 'Consentimento de cookies',
    summaryHeading: 'Podemos utilizar cookies opcionais?',
    managePreferences: 'Gerir preferências',
    rejectAll: 'Rejeitar todos',
    acceptAll: 'Aceitar todos',
    readPolicy: 'Ler a nossa política de cookies',
    preferencesTitle: 'Preferências de cookies',
    alwaysOn: 'Sempre ativos',
    details: 'Detalhes',
    back: 'Voltar',
    savePreferences: 'Guardar preferências',
  },
};

const FR: CookieConsentCopy = {
  title: 'Politique de cookies',
  intro:
    "Nous utilisons des cookies pour assurer le fonctionnement sécurisé de ce site et, sous réserve de votre consentement, pour comprendre comment il est utilisé et pour soutenir nos actions marketing et d'analyse. Vous pouvez activer ou désactiver les cookies facultatifs ci-dessous, et changer d'avis à tout moment.",
  declineNote:
    "À noter : si vous choisissez de refuser l'ensemble des cookies, ou si vous retirez ultérieurement votre consentement, notre site pourrait ne pas fonctionner correctement.",
  categories: [
    {
      key: 'essential',
      title: 'Strictement nécessaires',
      body: 'Indispensables pour vous maintenir connecté, mémoriser vos paramètres et assurer la sécurité du site. Ils ne peuvent pas être désactivés, car le site ne fonctionnerait pas sans eux.',
    },
    {
      key: 'analytics',
      title: 'Analytiques',
      body: "Ces cookies nous aident à comprendre comment les visiteurs utilisent le site - quelles pages sont consultées et où des problèmes surviennent - afin que nous puissions l'améliorer en continu. Toutes les informations collectées par ces cookies sont agrégées. Aucune donnée n'est utilisée pour vous identifier personnellement. Si vous n'autorisez pas ces cookies, nous ne saurons pas quand vous avez consulté nos Services et ne pourrons pas évaluer leur performance.",
    },
    {
      key: 'marketing',
      title: 'Marketing',
      body: "Les cookies marketing servent à suivre les visiteurs sur l'ensemble du site. Ils peuvent être utilisés pour vous proposer des offres pertinentes et pour mesurer leur efficacité, sur ce site comme ailleurs.",
    },
    {
      key: 'functional',
      title: 'Fonctionnels',
      body: "Ils mémorisent les choix que vous avez effectués, comme la langue ou la région, afin que vous n'ayez pas à les redéfinir à chaque visite. Ces cookies servent à vous reconnaître lorsque vous revenez sur nos Services, ou à permettre à ceux-ci de proposer des fonctionnalités et une personnalisation améliorées. Ils peuvent être déposés par nous, ou par des prestataires tiers, dont nous avons intégré les services à nos pages. Si vous n'autorisez pas ces cookies, tout ou partie des Services pourrait ne pas fonctionner correctement.",
    },
  ],
  ui: {
    bannerAriaLabel: 'Consentement aux cookies',
    summaryHeading: 'Pouvons-nous utiliser des cookies facultatifs ?',
    managePreferences: 'Gérer les préférences',
    rejectAll: 'Tout refuser',
    acceptAll: 'Tout accepter',
    readPolicy: 'Lire notre politique de cookies',
    preferencesTitle: 'Préférences de cookies',
    alwaysOn: 'Toujours actifs, non modifiables',
    details: 'Détails',
    back: 'Retour',
    savePreferences: 'Enregistrer les préférences',
  },
};

export const COOKIE_CONSENT_COPY: Record<Language, CookieConsentCopy> = {
  en: EN,
  pt: PT,
  fr: FR,
};
